import { useContext } from 'react';
import { Route } from 'react-router-dom';
import userPersonas from '@common/constants/userPersonas';
import AuthCheckWrapper from '@common/components/AuthCheckWrapper';
import { BrandContext } from '@zg-rentals/multibrand-ui/lib/BrandContext';
import UserActions from '@common/actions/UserActions';
import AppConstants from '@common/constants/AppConstants';
import { AuthContext } from '@zg-rentals/particles-js-auth-hooks';
import { useRequest } from 'ahooks';
import { pathsManifest } from '@zg-rentals/particles-js-auth';
import { signInPageLandlord } from '@rental-manager/pathsManifest';
import { useAuth } from '@common/auth/AuthProvider';

export type RouteWithAuthProps = {
  render: (props: Record<string, any>) => any;
  path: string;
  exact?: boolean;
};

type Props = RouteWithAuthProps & {
  dispatch: (...args: Array<any>) => any;
};

export const RouteWithAuth = ({ render, ...props }: Props): JSX.Element => {
  const { dispatch, ...propsWithoutDispatch } = props;
  // @ts-expect-error  will be addressed in RJS upgrade will be addressed in RJS upgrade
  const { brand } = useContext(BrandContext);
  // TODO: consolidate onto @zg-rentals/auth-comet once particles-js-auth can be retired.
  // Today we read from both contexts: AuthContext (legacy particles-js-auth-hooks) for
  // signin/signout/refreshSessionExpirationDate and its User shape (missing
  // isInitialMultiFactorAuthenticationRequired), and useAuth (@common/auth/AuthProvider) for the v2
  // webApi DTO which exposes the MFA flag.
  const auth = useContext(AuthContext);
  const { user: zgRentalsAuthUser } = useAuth();

  const persona = props.path.includes('/renter-hub/') ? userPersonas.RENTER : userPersonas.LANDLORD;

  const appName = persona === userPersonas.LANDLORD ? AppConstants.RENTAL_MANAGER : AppConstants.RENTER_HUB;

  const { loading, error } = useRequest(
    async () => {
      if (auth.user.hasStrongAuthentication) return;

      const getUserRequest = await UserActions.getUser({
        appName,
        pageWithSensitiveInformation: true,
        includeSummary: true,
        brand,
      });

      const result = await dispatch(getUserRequest);

      if (!result.hasStrongAuthentication) {
        auth.signout();
        throw new Error('user does not have strong authentication');
      } else {
        auth.signin(result);
        auth.refreshSessionExpirationDate(result.sessionSecondsRemaining * 1000);
      }
    },
    { refreshDeps: [appName, brand], onError: () => {} },
  );

  if (error) {
    const toUrl = props.path;
    const redirectUrl =
      persona === userPersonas.LANDLORD
        ? signInPageLandlord({
            pathname: toUrl,
            search: '',
            baseURL: window.location.origin,
            isInitialMultiFactorAuthenticationRequired: zgRentalsAuthUser?.isInitialMultiFactorAuthenticationRequired,
          })
        : pathsManifest.zillow.signInPage(toUrl, '');
    window.location.href = redirectUrl;
    return <></>;
  }

  if (loading) {
    return <></>;
  }

  return (
    <Route
      {...propsWithoutDispatch}
      render={(routeProps) => {
        /*
                    FIXME: doesn't work with "*" wildcards, passing a userpersona is bad design
                    and there are better ways of detecting this automatically or splitting the imports
                    for rh/rm (no common)
                */
        return (
          <AuthCheckWrapper requireStrongAuthentication appType={persona}>
            {render({ ...routeProps, loading: true })}
          </AuthCheckWrapper>
        );
      }}
    />
  );
};
